// js/sections/CollectionSection.js
import { Dom, Responsive, onSortGoTo } from 'utils';
import Delegate from 'ftdomdelegate';
import fastdom from 'fastdom';
import { CollectionFilterDrawer } from 'collection-filter-drawer';

class CollectionSection extends HTMLElement {
  constructor() {
    super();

    this.delegateElement = new Delegate(this);
    this.settings = JSON.parse(this.getAttribute("data-settings")) || {};
    this.currentUrl = new URL(window.location.href);
    this.mobileFilterDrawer = new CollectionFilterDrawer(this.settings);
    // abortController is created per-request in _reload; initialise to null here
    // so disconnectedCallback can safely check it.
    this.abortController = null;

    // Skip analytics/content-search side effects in theme editor design mode.
    // The section may reload multiple times per edit without user navigation.
    // (skill: cos-theme/theme-editor-events)
    if (!Shopify.designMode) {
      if (window.theme.pageType === "search") {
        this._loadContentResults();
      }
    }

    this._popstateHandler = () => {
      this.currentUrl = new URL(window.location.href);
      this._reload(false);
    };
    window.addEventListener("popstate", this._popstateHandler);
    this._attachListeners();
    this._setupFiltersModal();

    // Theme editor: re-attach delegate listeners when the editor reloads the section.
    // (skill: cos-theme/theme-editor-events)
    this._handleSectionLoad = this._onSectionLoad.bind(this);
    document.addEventListener('shopify:section:load', this._handleSectionLoad);
  }
  disconnectedCallback() {
    this.delegateElement.off();
    this.mobileFilterDrawer.destroy();
    window.removeEventListener("popstate", this._popstateHandler);
    document.removeEventListener('shopify:section:load', this._handleSectionLoad);
    // Cancel any in-flight collection reload fetch.
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
  connectedCallback() {
    // Re-initialize if needed when element is connected
  }
  _onSectionLoad(event) {
    // Called by the theme editor when the section is reloaded via the sidebar.
    // Re-bind delegate listeners so filter/sort/pagination work without a full page reload.
    if (event.target.contains(this)) {
      this.delegateElement.off();
      this._attachListeners();
    }
  }
  onSelect(event2) {
    if (Shopify.designMode && event2.detail.load) {
      this.querySelector(`.collection__layout-button[data-layout-mode="${this.settings["defaultLayout"]}"]`).click();
      this._showingCountChanged(this.settings["defaultProductsPerPage"]);
    }
  }
  _attachListeners() {
    this.delegateElement.on("click", '[data-action="change-layout"]', this._changeLayout.bind(this));
    this.delegateElement.on("change", "#mobile-sort-by, #desktop-sort-by", this._sortByChanged.bind(this));
    this.delegateElement.on("change", "#showing-count", this._showingCountChanged.bind(this));
    // value-picker-dialog.js emits cos:value-selected on the dialog. Bridge to existing handlers.
    this.delegateElement.on("cos:value-selected", "#sort-by-selector", (event) => this._sortByChanged(event.detail.value));
    this.delegateElement.on("cos:value-selected", "#display-by-selector", (event) => this._showingCountChanged(event.detail.value));
    this.delegateElement.on("click", ".pagination [data-page]", this._paginationPageChanged.bind(this));
    // Quick view functionality is handled by cos-product-item component
    this.delegateElement.on("change", '#desktop-filters-form input, #desktop-filters-form select', this._onFilterChanged.bind(this));
    this.delegateElement.on("click", '[data-action="clear-filters"]', this._onFiltersCleared.bind(this));
    // Price slider (cos-price-range, collection-grid.js) bubbles cos:price-changed here so the
    // section owns the single filter fetch (pushState + products-count + SEO head sync).
    this.delegateElement.on("cos:price-changed", "cos-price-range", (event) => this._onPriceChanged(event.detail));
  }



  /**
   * Switch layout mode
   */
  _changeLayout(event2, target) {
    if (target.classList.contains("is-selected")) {
      return;
    }
    const newLayoutMode = target.getAttribute("data-layout-mode");
    fetch(`${window.routes.cartUrl}/update.js`, {
      body: JSON.stringify({
        attributes: {
          "collection_layout": newLayoutMode
        }
      }),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
        // This is needed as currently there is a bug in Shopify that assumes this header
      }
    });
    fastdom.mutate(() => {
      Dom.getSiblings(target, ".is-selected").forEach((button) => button.classList.remove("is-selected"));
      target.classList.add("is-selected");
      this.querySelectorAll(".product-item").forEach((item) => {
        if (newLayoutMode === "grid") {
          item.className = `product-item product-item--vertical ${this.settings["gridClasses"]}`;
        } else {
          item.className = "product-item product-item--list";
        }
      });
    });
  }
  /**
   * Update the URL and reload products
   */
  _sortByChanged(value) {
    this.currentUrl.searchParams.set("sort_by", value);
    this.currentUrl.searchParams.delete("page");
    this._reload(true);
  }
  /**
   * When the number of items has changed
   */
  _showingCountChanged(value) {
    this.currentUrl.searchParams.delete("page");
    fetch(`${window.routes.cartUrl}/update.js`, {
      body: JSON.stringify({ attributes: { "collection_products_per_page": value } }),
      credentials: "same-origin",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
        // This is needed as currently there is a bug in Shopify that assumes this header
      }
    }).then(() => {
      this._reload(true);
    });
  }
  /**
   * When the page has changed
   */
  _paginationPageChanged(event2, target) {
    event2.preventDefault();
    this.currentUrl.searchParams.set("page", parseInt(target.getAttribute("data-page")));
    this._reload(true);
  }
  _onFilterChanged(event2, target) {
    const form = target.closest("form");
    
    // Pentru checkbox-uri, colectează toate valorile checked pentru acest nume
    if (target.type === 'checkbox') {
      const checkedValues = [];
      const inputsWithSameName = form.querySelectorAll(`input[name="${target.name}"]`);
      
      inputsWithSameName.forEach(input => {
        if (input.checked) {
          checkedValues.push(input.value);
        }
      });
      
      // Elimină toate valorile existente pentru acest parametru
      this.currentUrl.searchParams.delete(target.name);
      
      // Adaugă doar valorile checked
      checkedValues.forEach(value => {
        this.currentUrl.searchParams.append(target.name, value);
      });
    } else {
      // Pentru alte tipuri de input, actualizează valoarea
      if (target.value) {
        this.currentUrl.searchParams.set(target.name, target.value);
      }
    }
    
    // Resetează pagina când se schimbă filtrele
    this.currentUrl.searchParams.delete("page");
    
    this._reload(true);
  }
  _onFiltersCleared(event2, target) {
    this.currentUrl = new URL(target.getAttribute("data-url"), window.location.origin);
    this._reload(true);
  }
  /**
   * Price slider handoff (cos-price-range → cos:price-changed). Sets/clears the gte/lte
   * params, then reuses the standard reload path so price filtering behaves exactly like
   * every other filter (one fetch, pushState, count + SEO sync). A null min/max means the
   * handle is at its floor/ceiling → drop the param rather than write gte=0 / lte=max.
   */
  _onPriceChanged(detail) {
    if (!detail) return;
    const params = this.currentUrl.searchParams;
    if (detail.minName) {
      if (detail.min != null) params.set(detail.minName, detail.min);
      else params.delete(detail.minName);
    }
    if (detail.maxName) {
      if (detail.max != null) params.set(detail.maxName, detail.max);
      else params.delete(detail.maxName);
    }
    params.delete("page");
    this._reload(true);
  }
  /**
   * Reload all products from the current URL
   *
   * @private
   */
  _reload(pushState) {
    if (this.abortController) {
      this.abortController.abort();
    }
    if (pushState) {
      window.history.pushState({ path: this.currentUrl.toString() }, "", this.currentUrl.toString());
    }
    document.dispatchEvent(new CustomEvent("theme:loading:start"));
    const computedStyles = window.getComputedStyle(document.documentElement);
    let sectionUrl = "";
    
    // Determine the base URL based on the source type
    const sourceType = this.getAttribute("data-section-type");
    let baseUrl = this.currentUrl.pathname;
    
    if (sourceType === "search") {
      // For search, use the search URL
      baseUrl = window.routes.searchUrl || "/search";
    } else if (sourceType === "collection") {
      // For collection, use the current pathname (already set)
      baseUrl = this.currentUrl.pathname;
    }
    
    if (this.currentUrl.search) {
      sectionUrl = `${baseUrl}${this.currentUrl.search}&section_id=${this.settings.sectionId}`;
    } else {
      sectionUrl = `${baseUrl}?section_id=${this.settings.sectionId}`;
    }
    try {
      this.abortController = new AbortController();
      const currentAbortController = this.abortController;
      
      return fetch(sectionUrl, {
        credentials: "same-origin",
        method: "GET",
        signal: this.abortController.signal
      }).then((response) => {
        // Verifică dacă request-ul a fost anulat
        if (currentAbortController.signal.aborted) {
          return;
        }
        
        return response.text().then((content) => {
          // Verifică din nou dacă request-ul a fost anulat
          if (currentAbortController.signal.aborted) {
            return;
          }
          
          let tempElement = document.createElement("div");
          tempElement.innerHTML = content;

          this._applySeoFromSection(tempElement);

          // Actualizează data-products-count dacă există
          const productsCountElement = document.querySelector("[data-products-count]");
          if (productsCountElement) {
            // Încearcă să găsească valoarea din conținutul nou
            const newProductsCountElement = tempElement.querySelector("[data-products-count]");
            if (newProductsCountElement) {
              productsCountElement.innerHTML = newProductsCountElement.innerHTML;
            } else {
              // Fallback: folosește valoarea din settings dacă există
              if (this.settings.productsCount !== undefined) {
                productsCountElement.innerHTML = `(${this.settings.productsCount})`;
              }
            }
          }
          
          const refreshElements = this.querySelectorAll("[data-refresh]");
          refreshElements.forEach((element) => {
            const dataRefresh = element.getAttribute("data-refresh");
            if (!dataRefresh || dataRefresh.trim() === "") return;
            
            if (dataRefresh === "refresh_filters") {
              Array.from(element.querySelectorAll(".collection__filter-group-name[aria-controls]")).forEach((groupName) => {
                const newGroup = tempElement.querySelector(`[aria-controls="${groupName.getAttribute("aria-controls")}"]`);
                if (newGroup) {
                  if (groupName.getAttribute("aria-expanded") === "true") {
                    newGroup.setAttribute("aria-expanded", "true");
                    newGroup.nextElementSibling.setAttribute("aria-hidden", "false");
                    newGroup.nextElementSibling.style.height = "auto";
                    newGroup.nextElementSibling.style.overflow = "visible";
                  } else {
                    newGroup.setAttribute("aria-expanded", "false");
                    newGroup.nextElementSibling.setAttribute("aria-hidden", "true");
                    newGroup.nextElementSibling.style = "";
                  }
                }
              });
            }
 
            const refreshSource = tempElement.querySelector(`[data-refresh="${dataRefresh}"]`);
            if (refreshSource) {
              element.innerHTML = refreshSource.innerHTML;
            }
          });

          // Scroll back up to the top of the collection so the user sees the
          // refreshed grid + active filter chips, not blank space below.
          onSortGoTo();
          // this.querySelector(".collection__dynamic-part").innerHTML = tempElement.querySelector(".collection__dynamic-part").innerHTML;
          // this.innerHTML = tempElement.innerHTML;
          // let desktopFilters = this.querySelector("[data-filters-container]");
          // let mobileFilters = this.querySelector("#mobile-collection-filters");
          // let previousMobileScrollTop = 0;
          // if (mobileFilters) {
          //   previousMobileScrollTop = mobileFilters.querySelector(".collection-drawer__inner").scrollTop;
          // }
          // if (desktopFilters) {
            // const tempFiltersContainer = tempElement.querySelector("[data-filters-container]");
            // if (tempFiltersContainer) {
            //   desktopFilters.innerHTML = tempFiltersContainer.innerHTML;
            // }
            // mobileFilters.innerHTML = tempElement.querySelector("#mobile-collection-filters").innerHTML;
            // mobileFilters.querySelector(".collection-drawer__inner").scrollTop = previousMobileScrollTop;
            // this.querySelector("[data-active-filters-bar]").innerHTML = tempElement.querySelector("[data-active-filters-bar]").innerHTML;
            // this.mobileFilterDrawer._computeDrawerHeight();
          // }
          // const elementOffset = this.querySelector(".collection").getBoundingClientRect().top - 25 - parseInt(computedStyles.getPropertyValue("--header-is-sticky")) * parseInt(computedStyles.getPropertyValue("--header-height"));
          // if (elementOffset < 0) {
          //   window.scrollBy({ top: elementOffset, behavior: "smooth" });
          // }
          // const countJson = JSON.parse(tempElement.querySelector("[data-collection-products-count]").innerHTML);
          // let showingProductsCount = this.querySelector(".collection__products-count-showing");
          // if (showingProductsCount) {
          //   showingProductsCount.innerHTML = countJson["showingCount"];
          // }
          // this.querySelector(".collection__products-count-total").innerHTML = countJson["productsCount"];
          document.dispatchEvent(new CustomEvent("theme:loading:end"));
        });
      }).catch((error) => {
        // Gestionează erorile de abort fără să le logheze ca erori
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Error loading collection:', error);
        document.dispatchEvent(new CustomEvent("theme:loading:end"));
      });
    } catch (e) {
      console.error('Error in _reload:', e);
      document.dispatchEvent(new CustomEvent("theme:loading:end"));
    }
  }
  /**
   * ---------------------------------------------------------------------------------------------------
   * FILTERS MODAL SETUP
   * ---------------------------------------------------------------------------------------------------
   */
  _setupFiltersModal() {
    // Mută conținutul din data-filters-container în modal doar de la 999px în jos
    const filtersContainer = this.querySelector('[data-filters-container]');
    const filtersSection = this.querySelector('.layout__section--secondary.hidden-tablet-and-down');
    
    // Caută modalul cu un selector mai general
    const modal = document.querySelector(`#modal-filters-${this.settings.sectionId}`);
    const modalContent = modal ? modal.querySelector('[id*="modal-filters-content"]') : null;
    
    if (filtersContainer && modalContent) {
      // Gestionează afișarea bazată pe media query - mută conținutul doar când e sub 999px
      const mediaQuery = window.matchMedia('(max-width: 999px)');
      const handleMediaChange = (e) => {
        if (e.matches) {
          // Pe mobile/tablet (999px și mai puțin) - mută conținutul în modal și ascunde secțiunea parent
          if (filtersContainer.parentNode !== modalContent && modalContent.innerHTML === '') {
            // Array.from(modalContent.querySelectorAll(".collection__filter-group-name[aria-controls]")).forEach((groupName) => {
            //   let newGroup = filtersContainer.querySelector(`[aria-controls="${groupName.getAttribute("aria-controls")}"]`);
            //   if (newGroup) {
            //     if (groupName.getAttribute("aria-expanded") === "true") {
            //       newGroup.setAttribute("aria-expanded", "true");
            //       newGroup.nextElementSibling.setAttribute("aria-hidden", "false");
            //       newGroup.nextElementSibling.style.height = "auto";
            //       newGroup.nextElementSibling.style.overflow = "visible";
            //     } else {
            //       newGroup.setAttribute("aria-expanded", "false");
            //       newGroup.nextElementSibling.setAttribute("aria-hidden", "true");
            //       newGroup.nextElementSibling.style = "";
            //     }
            //   }
            // });
            // modalContent.innerHTML = '';
            modalContent.appendChild(filtersContainer);
          }
          if (filtersSection) {
            filtersSection.style.display = 'none';
          }
        } else {
          // Pe desktop (1000px și mai mult) - mută conținutul înapoi și afișează secțiunea parent
          if (filtersSection && filtersContainer.parentNode === modalContent) {
            // filtersSection.innerHTML = '';
            filtersSection.appendChild(filtersContainer);
          }
          if (filtersSection) {
            filtersSection.style.display = '';
          }
        }
      };
      
      mediaQuery.addEventListener('change', handleMediaChange);
      handleMediaChange(mediaQuery); // Verifică starea inițială
    }
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * SEARCH PAGE SPECIFIC METHOD
   * ---------------------------------------------------------------------------------------------------
   */
  _loadContentResults() {
    let currentUrl = new URL(window.location.href);
    fetch(`${window.routes.searchUrl}?section_id=search-content&q=${currentUrl.searchParams.get("q")}&type=article,page`, {
      credentials: "same-origin"
    }).then((response) => {
      response.text().then((content) => {
        let linkSearchResults = this.querySelector(".link-search-results"), fakeDiv = document.createElement("div");
        fakeDiv.innerHTML = content;
        if (linkSearchResults && content.trim() !== "") {
          linkSearchResults.innerHTML = fakeDiv.firstElementChild.innerHTML;
          linkSearchResults.style.display = "block";
        }
      });
    });
  }

  /**
   * ---------------------------------------------------------------------------------------------------
   * SEO HEAD + H1 SYNC ON FILTER CHANGE
   * Reads the new section's <cos-collection data-settings={...,"seo":{...}}> JSON and patches
   * document.title, meta description/robots, canonical, og/twitter title+description, and the H1
   * in the sibling main-collection-banner section. No extra network request.
   * ---------------------------------------------------------------------------------------------------
   */
  _applySeoFromSection(tempElement) {
    const newRoot = tempElement.querySelector("cos-collection");
    if (!newRoot) return;

    let seo;
    try {
      seo = JSON.parse(newRoot.dataset.settings || "{}").seo;
    } catch (e) {
      return;
    }
    if (!seo) return;

    if (seo.title) {
      document.title = seo.title;
    }

    this._setMetaName("description", seo.description);

    if (seo.robots === "noindex,follow") {
      this._setMetaName("robots", "noindex,follow");
    } else {
      this._removeMetaName("robots");
    }

    if (seo.canonical) {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", seo.canonical);
    }

    if (seo.title) {
      this._setMetaProperty("og:title", seo.title);
      this._setMetaName("twitter:title", seo.title);
    }
    if (seo.description) {
      this._setMetaProperty("og:description", seo.description);
    }

    const h1Text = seo.h1 || seo.h1Default;
    if (h1Text) {
      const bannerTitle = document.querySelector(".collection-banner__title");
      if (bannerTitle) {
        const desktopSpan = bannerTitle.querySelector(".hidden-phone");
        const mobileSpan = bannerTitle.querySelector(".hidden-tablet-up");
        if (desktopSpan) desktopSpan.textContent = h1Text;
        if (mobileSpan) mobileSpan.textContent = h1Text;
      }
    }
  }

  _setMetaName(name, content) {
    if (content === null || content === undefined || content === "") {
      this._removeMetaName(name);
      return;
    }
    let el = document.head.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  }

  _removeMetaName(name) {
    const el = document.head.querySelector(`meta[name="${name}"]`);
    if (el) el.remove();
  }

  _setMetaProperty(property, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[property="${property}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("property", property);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  }
}
if (!window.customElements.get("cos-collection")) window.customElements.define("cos-collection", CollectionSection);
